import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import { TypeGuard, type Timezone } from '@principle-theorem/shared';
import { flow, isBoolean, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';

export const PATIENT_MEDICAL_HISTORY_RESOURCE_TYPE = 'patientMedicalHistory';

export const PATIENT_MEDICAL_HISTORY_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Medical History List',
      description: '',
      idPrefix: PATIENT_MEDICAL_HISTORY_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface IPraktikaMedicalHistory {
  patient_id: string;
  item_id: string;
  is_present: boolean;
  value: string | null;
  effective_from: string | null;
}

export type IPraktikaMedicalHistoryTranslations = Record<string, never>;

export interface IPraktikaMedicalHistoryFilters {
  patientId: string;
}

function isPraktikaMedicalHistory(
  item: unknown,
  logError: boolean
): item is IPraktikaMedicalHistory {
  return TypeGuard.interface<IPraktikaMedicalHistory>(
    {
      patient_id: isString,
      item_id: isString,
      is_present: isBoolean,
      value: TypeGuard.nilOr(isString),
      effective_from: TypeGuard.nilOr(isString),
    },
    logError
  )(item);
}

export type PraktikaMedicalHistory = ISourceEntityTypes<
  IPraktikaMedicalHistory,
  IPraktikaMedicalHistoryTranslations,
  IPraktikaMedicalHistoryFilters
>;

/**
 * Rows where bpresent=false AND vchvalue is blank are skipped at query level
 * to avoid migrating pure "answered no" noise.
 */
const PATIENT_MEDICAL_HISTORY_SOURCE_QUERY = `
SELECT
  ipatientnumber::TEXT AS patient_id,
  vchitemid AS item_id,
  COALESCE(bpresent, false) AS is_present,
  NULLIF(TRIM(vchvalue), '') AS value,
  dtefffrom AS effective_from
FROM patient_medical_history
WHERE ipatientnumber IS NOT NULL
  AND (bpresent = true OR NULLIF(TRIM(vchvalue), '') IS NOT NULL)
ORDER BY ipatientnumber, vchitemid
${OFFSET_PLACEHOLDER}
`;

const PATIENT_MEDICAL_HISTORY_ESTIMATE_QUERY = `
SELECT
  ipatientnumber::TEXT AS patient_id,
  vchitemid AS item_id,
  COALESCE(bpresent, false) AS is_present,
  NULLIF(TRIM(vchvalue), '') AS value,
  dtefffrom AS effective_from
FROM patient_medical_history
WHERE ipatientnumber IS NOT NULL
  AND (bpresent = true OR NULLIF(TRIM(vchvalue), '') IS NOT NULL)
`;

export class PatientMedicalHistorySourceEntity extends BaseSourceEntity<PraktikaMedicalHistory> {
  sourceEntity = PATIENT_MEDICAL_HISTORY_SOURCE_ENTITY;
  entityResourceType = PATIENT_MEDICAL_HISTORY_RESOURCE_TYPE;
  sourceQuery = PATIENT_MEDICAL_HISTORY_SOURCE_QUERY;
  override estimateQuery = PATIENT_MEDICAL_HISTORY_ESTIMATE_QUERY;
  verifySourceFn = isPraktikaMedicalHistory;
  override transformDataFn = flow([]);

  translate(
    _data: IPraktikaMedicalHistory,
    _timezone: Timezone
  ): IPraktikaMedicalHistoryTranslations {
    return {};
  }

  getSourceRecordId(data: IPraktikaMedicalHistory): string {
    return `${data.patient_id}-${data.item_id}`;
  }

  getSourceLabel(data: IPraktikaMedicalHistory): string {
    return `${data.patient_id} - ${data.item_id}`;
  }

  getFilterData(
    data: IPraktikaMedicalHistory,
    _timezone: Timezone
  ): IPraktikaMedicalHistoryFilters {
    return {
      patientId: data.patient_id,
    };
  }
}
